import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useFillerWords } from './useFillerWords';
import { classnames } from '@/libs/utils';
import IconButton from '@/components/atoms/Button/IconButton';
import TooltipV2 from '@/components/TooltipV2';

const FillerWordsMenu: React.FC<{
  onEditActionTriggered?: () => void;
  onCloseMenu?: () => void;
}> = React.memo(({ onEditActionTriggered, onCloseMenu }) => {
  const [isFillerWordsMenuOpen, setIsFillerWordsMenuOpen] = useState(false);
  const fillerWordsMenuRef = useRef<HTMLDivElement>(null);
  const fillerWordsMenuButtonRef = useRef<HTMLDivElement>(null);

  const {
    hasFillerWords,
    hasFillerWordsDeleted,
    fillerWordsCount,
    fillerWordsDuration,
    handleApplyDeletes,
    handleRevertDeletes
  } = useFillerWords(onEditActionTriggered, onCloseMenu, setIsFillerWordsMenuOpen);

  const shouldShowFillerWordsMenu = useMemo(() => {
    return hasFillerWords || hasFillerWordsDeleted;
  }, [hasFillerWords, hasFillerWordsDeleted]);

  const toggleMenu = useCallback(() => {
    setIsFillerWordsMenuOpen(prev => !prev);
  }, []);

  const getTooltipContent = useMemo(() => {
    if (!shouldShowFillerWordsMenu) {
      return 'No filler words detected in this video.';
    }
    if (hasFillerWordsDeleted) {
      return 'Filler words removed. Click to revert changes.';
    }
    return 'Remove filler words';
  }, [shouldShowFillerWordsMenu, hasFillerWordsDeleted]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        fillerWordsMenuRef.current &&
        !fillerWordsMenuRef.current.contains(event.target as Node) &&
        fillerWordsMenuButtonRef.current &&
        !fillerWordsMenuButtonRef.current.contains(event.target as Node)
      ) {
        setIsFillerWordsMenuOpen(false);
      }
    };

    if (isFillerWordsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isFillerWordsMenuOpen]);

  return (
    <>
      <div
        className={classnames(
          'flex h-8 flex-none rounded-lg transition-all duration-500',
          isFillerWordsMenuOpen
            ? 'w-8 items-center justify-center border bg-deep-orange-50'
            : 'w-8 items-center justify-center border hover:border-deep-orange-50 hover:bg-deep-orange-50 hover:text-deep-orange-600'
        )}
        ref={fillerWordsMenuButtonRef}
      >
        <TooltipV2
          tooltipElement={
            <IconButton
              icon="IconEraser"
              variation="outline"
              size="base"
              disabled={!shouldShowFillerWordsMenu}
              onClick={toggleMenu}
              buttonClassName="size-8"
              trackingId="filler-words-menu-button"
            />
          }
          tooltipId="filler-words-menu-tooltip"
          tooltipContent={getTooltipContent}
          colorTheme="dark"
        />
      </div>

      {isFillerWordsMenuOpen && (
        <div
          ref={fillerWordsMenuRef}
          className="w-100 absolute right-0 top-14 z-50 rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          <div className="space-y-2 p-2">
            <div className="gap-2 p-1.5">
              <div className="text-sm font-medium">Remove filler words</div>
              <div className="text-muted-foreground text-xs">
                Cuts all ums, ahs, and other filler and repeated words.
              </div>
            </div>

            <div className="flex items-center gap-4 pl-1.5">
              {!hasFillerWordsDeleted && fillerWordsCount && (
                <div className="shrink-0 text-xs">
                  <span className="font-medium">{`${fillerWordsCount.toLocaleString()} words`}</span> (
                  {fillerWordsDuration})
                </div>
              )}
              {hasFillerWordsDeleted ? (
                <IconButton
                  icon="IconRotate"
                  content="Revert"
                  variation="outline"
                  size="base"
                  disabled={!hasFillerWordsDeleted}
                  onClick={handleRevertDeletes}
                  buttonClassName="w-2/3 ml-auto"
                  trackingId="filler-words-revert-button"
                />
              ) : (
                <IconButton
                  icon="IconScissors"
                  content="Cut"
                  variation="filled"
                  size="base"
                  disabled={fillerWordsCount === 0}
                  onClick={handleApplyDeletes}
                  buttonClassName="w-full"
                  trackingId="filler-words-cut-button"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default FillerWordsMenu;
